import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { FilterSchema } from '@/api';
import { usePatchStdMapId, useStdMap } from '@/api/queries/std';
import { Button } from '@/components/Button/Button';
import { DocumentTitle } from '@/components/DocumentTitle';
import { DropdownMenu } from '@/components/DropdownMenu/DropdownMenu';
import { Explorer } from '@/components/Explorer';
import { ModalTrigger } from '@/components/Modal';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { eStrings } from '@/constants/strings';
import { StandaardtekstMapAdd } from '@/modals/StandaardtekstMapAdd';
import { StandaardtekstMapEdit } from '@/modals/StandaardtekstMapEdit';
import { useUserStore } from '@/stores/user';
import { useIsExactMatch } from '@/utils/hooks/useIsExactMatch';

import { GrondslagTabs } from './-components/GrondslagTabs';
import { SidebarLayout } from './-components/SidebarLayout';

const searchSchema = z.object({
  p: z.number().optional().default(1),
});

type SearchParams = z.infer<typeof searchSchema>;

/**
 * Query options
 */
const mapQueryOptions = (
  search: SearchParams,
  isOD: boolean,
  grondslagId: string
) => {
  const filters: FilterSchema[] = [
    {
      field: 'Map',
      op: 'is_null',
      value: '',
    },
    {
      field: 'Grondslag',
      op: 'eq',
      value: grondslagId,
    },
  ];

  if (isOD) {
    filters.push({
      field: 'Landelijk',
      op: 'eq',
      value: '0',
    });
  }

  return useStdMap.getOptions({
    page: search.p,
    size: 20,
    sort: [{ field: 'Naam', direction: 'asc' }],
    filter: filters,
  });
};

/**
 * Route
 */
export const Route = createFileRoute('/beheer/standaardteksten/$grondslagId')({
  validateSearch: searchSchema.parse,
  beforeLoad: () => ({
    getTitle: () => 'Standaardteksten',
  }),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context: { queryClient }, deps: { search }, params }) => {
    const isOD = useUserStore.getState().area === 'omgevingsdienst';

    try {
      await queryClient.ensureQueryData(
        mapQueryOptions(search, isOD, params.grondslagId)
      );
    } catch {}
  },
  component: StandaardtekstenComponent,
});

/**
 * Route component
 */
function StandaardtekstenComponent() {
  const search = Route.useSearch();
  const params = Route.useParams();

  const isOD = useUserStore(({ area }) => area === 'omgevingsdienst');

  const mappen = useQuery(mapQueryOptions(search, isOD, params.grondslagId));
  const patchMapId = usePatchStdMapId();

  const isExactMatch = useIsExactMatch(Route.fullPath);

  if (!isExactMatch) {
    return <Outlet />;
  }

  if (!mappen.data) return null;

  return (
    <SidebarLayout testId={Route.fullPath}>
      <DocumentTitle
        sections={[eStrings.LBL_DIGI_V, 'Beheer Standaardteksten']}
      />

      <GrondslagTabs>
        <Explorer
          title="Mappen"
          button={
            <ModalTrigger
              modalComponent={StandaardtekstMapAdd}
              onOpen={() => ({ grondslagId: params.grondslagId })}
              asChild
            >
              <Button variant="secondary">Nieuwe map</Button>
            </ModalTrigger>
          }
        >
          {!mappen.data.objects?.length ? (
            <Explorer.Placeholder>
              Dit vergunningdeel heeft geen mappen.
            </Explorer.Placeholder>
          ) : (
            <ul>
              {mappen.data.objects?.map((map) => (
                <li key={map.ID}>
                  <Explorer.Button
                    asChild
                    icon="folder"
                    inactive={map.Status === 'Archief'}
                    dropdownMenuContent={
                      <>
                        <ModalTrigger
                          modalComponent={StandaardtekstMapEdit}
                          onOpen={() => ({ map })}
                          asChild
                        >
                          <DropdownMenu.Button>Bewerken</DropdownMenu.Button>
                        </ModalTrigger>

                        <DropdownMenu.Button
                          onClick={() => {
                            patchMapId.mutate({
                              id: map.ID!,
                              data: {
                                Status:
                                  map.Status === 'Actief'
                                    ? 'Archief'
                                    : 'Actief',
                              },
                            });
                          }}
                        >
                          {map.Status === 'Actief'
                            ? 'Onzichtbaar maken'
                            : 'Zichtbaar maken'}
                        </DropdownMenu.Button>
                      </>
                    }
                  >
                    <Link
                      to="./$mapId"
                      from={Route.id}
                      params={{ mapId: map.ID! }}
                      search={{ p: 1 }}
                    >
                      {map.Naam}
                      {map.Status === 'Archief'
                        ? ' (onzichtbaar voor gebruikers)'
                        : ''}
                    </Link>
                  </Explorer.Button>
                </li>
              ))}
            </ul>
          )}
        </Explorer>
      </GrondslagTabs>

      {(mappen.data.num_pages ?? 0) > 1 ? (
        <div className="mt-5 flex justify-center">
          <PaginationLinks
            aria-label="Standaardtekst mappen pagina's"
            renderLink={({ children, page }) => (
              <Link to="." from={Route.id} search={{ p: page }}>
                {children}
              </Link>
            )}
            currentPage={search.p}
            totalPages={mappen.data.num_pages ?? 0}
          />
        </div>
      ) : null}
    </SidebarLayout>
  );
}
